import { useEffect } from 'react';
import { useSetError } from '@src/redux/modals';
import { ConfigSummary } from '../data-qa-types';
import { ModelWithStages, useAllModelStages } from './useAllModelStages';

export interface AllModelsExport {
  models: ModelWithStages[];
  // True while models load, when there are none, or when any failed to load.
  unavailable: boolean;
}

// Shared wiring for the one-sheet-per-model Excel exports. Blocks the export outright when
// any model failed to load -- a workbook silently missing a model's sheet would read as
// complete -- and says so once via the error modal. `exportLabel` names the export in that
// report (e.g. "Campaign export").
export const useAllModelsExport = (
  company: string,
  configs: ConfigSummary[],
  exportLabel: string
): AllModelsExport => {
  const { loading, models, failedModelLabels } = useAllModelStages(company, configs);
  const setError = useSetError();
  useEffect(() => {
    if (failedModelLabels.length === 0) return;
    setError({
      title: "Couldn't load every model for export",
      message: `Excel export is unavailable because ${failedModelLabels.join(', ')} failed to load. Reload the page to retry.`,
      reportError: new Error(`${exportLabel}: failed to load ${failedModelLabels.join(', ')}`)
    });
  }, [failedModelLabels, exportLabel, setError]);

  return {
    models,
    unavailable: loading || models.length === 0 || failedModelLabels.length > 0
  };
};
